import { useEffect, useState, useMemo } from 'react';
import { Typography } from 'antd';
import styled from 'styled-components';
import { DeviceType, TextLinkButton, styleVariables, useDeviceType, AudioPlayerV2, MathEnabledTextComponent, getSize } from '@lib/common';
import { CloseOutlined, SoundFilled } from '@ant-design/icons';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var Paragraph = Typography.Paragraph,
    Title = Typography.Title;
var decoratorHeight = '40px';
var Decorator = styled.div.withConfig({
  displayName: "submission-sectioncomponent__Decorator",
  componentId: "sc-lywxuf-0"
})(["position:absolute;top:-", ";left:0;padding:8px 16px;width:auto;height:", ";border-radius:10px 10px 0px 0px;font-size:16px;font-weight:400;color:", ";text-align:center;background:", ";"], decoratorHeight, decoratorHeight, styleVariables.colors.gray1, styleVariables.colors.blue3);
var Content = styled.div.withConfig({
  displayName: "submission-sectioncomponent__Content",
  componentId: "sc-lywxuf-1"
})(["display:flex;justify-content:space-between;align-items:center;"]);
var Description = styled(Paragraph).withConfig({
  displayName: "submission-sectioncomponent__Description",
  componentId: "sc-lywxuf-2"
})(["&.ant-typography{margin-top:16px;margin-bottom:0;font-style:normal;font-weight:400;font-size:16px;line-height:24px;color:", ";}img{max-width:100%;}"], styleVariables.colors.gray13);
var StyledTitle = styled(Title).withConfig({
  displayName: "submission-sectioncomponent__StyledTitle",
  componentId: "sc-lywxuf-3"
})(["&.ant-typography{margin-bottom:0;font-style:normal;font-size:24px;line-height:40px;color:", ";}"], styleVariables.colors.gray13);
var Score = styled.label.withConfig({
  displayName: "submission-sectioncomponent__Score",
  componentId: "sc-lywxuf-4"
})(["font-style:normal;font-weight:400;font-size:14px;line-height:24px;padding-left:12px;color:", ";"], styleVariables.colors.gray10);
var StyledLeftHeader = styled.div.withConfig({
  displayName: "submission-sectioncomponent__StyledLeftHeader",
  componentId: "sc-lywxuf-5"
})(["width:100%;display:flex;align-items:center;"]);
var StyledHeaderTitle = styled.div.withConfig({
  displayName: "submission-sectioncomponent__StyledHeaderTitle",
  componentId: "sc-lywxuf-6"
})(["display:flex;align-items:center;"]);
var StyledSoundOutlined = styled(SoundFilled).withConfig({
  displayName: "submission-sectioncomponent__StyledSoundOutlined",
  componentId: "sc-lywxuf-7"
})(["font-size:22px;margin-left:15px;cursor:pointer;"]);
var Layout = styled.div.withConfig({
  displayName: "submission-sectioncomponent__Layout",
  componentId: "sc-lywxuf-8"
})(["display:flex;flex-direction:column;position:relative;border-radius:0px 12px 12px 12px;padding:24px;box-shadow:0px 3px 6px rgba(0,0,0,0.12);background:", ";margin-bottom:", ";display:flex;"], styleVariables.colors.gray1, getSize('padding16'));
export var SubmissionSectionComponent = function SubmissionSectionComponent(_ref) {
  var _section$audioFile2, _section$audioFile5, _section$audioFile5$r, _section$audioFile5$r2, _section$audioFile7, _section$audioFile7$r, _section$audioFile7$r2, _section$audioFile8, _section$audioFile$ru2, _section$audioFile9, _section$audioFile9$r, _section$audioFile$ru3, _section$audioFile10, _section$audioFile10$, _section$audioFile$ru4, _section$audioFile11, _section$audioFile11$;

  var section = _ref.section,
      audioStats = _ref.audioStats,
      sectionIndex = _ref.sectionIndex,
      totalScore = _ref.totalScore,
      label = _ref.label,
      PinIcon = _ref.PinIcon,
      onPinSection = _ref.onPinSection,
      pinnedSectionId = _ref.pinnedSectionId,
      onCancelPin = _ref.onCancelPin,
      sectionAudioPlayerProps = _ref.sectionAudioPlayerProps,
      audioPlayerLabels = _ref.audioPlayerLabels,
      hideScore = _ref.hideScore,
      onTrackAudioPlay = _ref.onTrackAudioPlay,
      noTrickyAudioRule = _ref.noTrickyAudioRule,
      _ref$readonly = _ref.readonly,
      readonly = _ref$readonly === void 0 ? false : _ref$readonly;

  var _useState = useState(true),
      hasPin = _useState[0],
      setHasPin = _useState[1];

  var deviceType = useDeviceType();
  var sectionId = "section-".concat(section.id.toString(), "-").concat(sectionIndex);
  var audioStatsForSection = useMemo(function () {
    return audioStats === null || audioStats === void 0 ? void 0 : audioStats.find(function (stat) {
      var _section$audioFile;

      return stat.audioFileId === ((_section$audioFile = section.audioFile) === null || _section$audioFile === void 0 ? void 0 : _section$audioFile.id.toString());
    });
  }, [audioStats, (_section$audioFile2 = section.audioFile) === null || _section$audioFile2 === void 0 ? void 0 : _section$audioFile2.id]);
  var jsonAudioFile = section === null || section === void 0 ? void 0 : section.audioFile;
  var numberOfReplaysRemaining = useMemo(function () {
    var _section$audioFile3, _section$audioFile3$r, _section$audioFile3$r2, _section$audioFile$ru, _section$audioFile4, _section$audioFile4$r, _section$audioFile4$r2;

    if (noTrickyAudioRule) return -1;
    var ruleVal = (_section$audioFile3 = section.audioFile) === null || _section$audioFile3 === void 0 ? void 0 : (_section$audioFile3$r = _section$audioFile3.rule) === null || _section$audioFile3$r === void 0 ? void 0 : (_section$audioFile3$r2 = _section$audioFile3$r.numberOfReplays) === null || _section$audioFile3$r2 === void 0 ? void 0 : _section$audioFile3$r2.value;
    if (ruleVal === -1) return -1;

    if (ruleVal != null && audioStats != null) {
      var used = audioStatsForSection === null || audioStatsForSection === void 0 ? void 0 : audioStatsForSection.getConsumedAudioPlayEventsCount(audioStatsForSection);
      return Math.max(0, ruleVal - used);
    }

    return (_section$audioFile$ru = (_section$audioFile4 = section.audioFile) === null || _section$audioFile4 === void 0 ? void 0 : (_section$audioFile4$r = _section$audioFile4.rule) === null || _section$audioFile4$r === void 0 ? void 0 : (_section$audioFile4$r2 = _section$audioFile4$r.numberOfReplays) === null || _section$audioFile4$r2 === void 0 ? void 0 : _section$audioFile4$r2.value) !== null && _section$audioFile$ru !== void 0 ? _section$audioFile$ru : 1;
  }, [noTrickyAudioRule, audioStats, audioStatsForSection, (_section$audioFile5 = section.audioFile) === null || _section$audioFile5 === void 0 ? void 0 : (_section$audioFile5$r = _section$audioFile5.rule) === null || _section$audioFile5$r === void 0 ? void 0 : (_section$audioFile5$r2 = _section$audioFile5$r.numberOfReplays) === null || _section$audioFile5$r2 === void 0 ? void 0 : _section$audioFile5$r2.value]);
  var replayBudgetFromServer = useMemo(function () {
    var _section$audioFile6, _section$audioFile6$r, _section$audioFile6$r2;

    var v = (_section$audioFile6 = section.audioFile) === null || _section$audioFile6 === void 0 ? void 0 : (_section$audioFile6$r = _section$audioFile6.rule) === null || _section$audioFile6$r === void 0 ? void 0 : (_section$audioFile6$r2 = _section$audioFile6$r.numberOfReplays) === null || _section$audioFile6$r2 === void 0 ? void 0 : _section$audioFile6$r2.value;
    return v != null && v !== -1 && audioStats != null;
  }, [audioStats, (_section$audioFile7 = section.audioFile) === null || _section$audioFile7 === void 0 ? void 0 : (_section$audioFile7$r = _section$audioFile7.rule) === null || _section$audioFile7$r === void 0 ? void 0 : (_section$audioFile7$r2 = _section$audioFile7$r.numberOfReplays) === null || _section$audioFile7$r2 === void 0 ? void 0 : _section$audioFile7$r2.value]);
  useEffect(function () {
    if (deviceType === DeviceType.mobile) {
      setHasPin(false);
    } else if (deviceType === DeviceType.desktop) {
      setHasPin(true);
    }
  }, [deviceType]);
  return /*#__PURE__*/_jsxs(Layout, {
    children: [/*#__PURE__*/_jsxs(Decorator, {
      children: [label.section, " ", sectionIndex + 1]
    }), /*#__PURE__*/_jsxs(Content, {
      children: [/*#__PURE__*/_jsxs(StyledLeftHeader, {
        children: [/*#__PURE__*/_jsxs(StyledHeaderTitle, {
          children: [/*#__PURE__*/_jsx(StyledTitle, {
            level: 2,
            children: section.title
          }), !hideScore && /*#__PURE__*/_jsxs(Score, {
            children: [totalScore, " ", label.score]
          })]
        }), (sectionAudioPlayerProps === null || sectionAudioPlayerProps === void 0 ? void 0 : sectionAudioPlayerProps.hideAudioPlayer) && /*#__PURE__*/_jsx(StyledSoundOutlined, {})]
      }), hasPin && /*#__PURE__*/_jsx(_Fragment, {
        children: pinnedSectionId === section.id.toString() ? /*#__PURE__*/_jsx(TextLinkButton, {
          icon: /*#__PURE__*/_jsx(CloseOutlined, {}),
          onClick: function onClick() {
            return onCancelPin();
          }
        }) : /*#__PURE__*/_jsx(TextLinkButton, {
          icon: PinIcon,
          onClick: function onClick() {
            return onPinSection(section.id.toString());
          }
        })
      })]
    }), !readonly && (jsonAudioFile === null || jsonAudioFile === void 0 ? void 0 : jsonAudioFile.url) && /*#__PURE__*/_jsx(AudioPlayerV2, {
      source: (_section$audioFile8 = section.audioFile) === null || _section$audioFile8 === void 0 ? void 0 : _section$audioFile8.url,
      audioId: sectionId,
      audioPlayerConfig: {
        isAllowedToReplay: noTrickyAudioRule ? true : (_section$audioFile$ru2 = (_section$audioFile9 = section.audioFile) === null || _section$audioFile9 === void 0 ? void 0 : (_section$audioFile9$r = _section$audioFile9.rule) === null || _section$audioFile9$r === void 0 ? void 0 : _section$audioFile9$r.isAllowedToReplay) !== null && _section$audioFile$ru2 !== void 0 ? _section$audioFile$ru2 : false,
        isAllowedToPause: noTrickyAudioRule ? true : (_section$audioFile$ru3 = (_section$audioFile10 = section.audioFile) === null || _section$audioFile10 === void 0 ? void 0 : (_section$audioFile10$ = _section$audioFile10.rule) === null || _section$audioFile10$ === void 0 ? void 0 : _section$audioFile10$.allowingToPauseAndStop) !== null && _section$audioFile$ru3 !== void 0 ? _section$audioFile$ru3 : true,
        isAutoPlay: noTrickyAudioRule ? false : (_section$audioFile$ru4 = (_section$audioFile11 = section.audioFile) === null || _section$audioFile11 === void 0 ? void 0 : (_section$audioFile11$ = _section$audioFile11.rule) === null || _section$audioFile11$ === void 0 ? void 0 : _section$audioFile11$.isAutoPlay) !== null && _section$audioFile$ru4 !== void 0 ? _section$audioFile$ru4 : false,
        numberOfReplays: numberOfReplaysRemaining,
        decrementReplaysOnEnded: !replayBudgetFromServer,
        onTrackAudioPlay: onTrackAudioPlay
      },
      labels: audioPlayerLabels
    }), /*#__PURE__*/_jsx(Description, {
      children: /*#__PURE__*/_jsx(MathEnabledTextComponent, {
        value: section.description
      })
    })]
  });
};